import api, { API } from "@/lib/api";

/**
 * Client for Dany's HTTP surface (DY1.3 backend, DY1.4 shell).
 *
 *   POST /api/dany/chat                 create or continue a thread, SSE stream
 *   GET  /api/dany/threads              the caller's conversations
 *   GET  /api/dany/threads/{thread_id}  one full transcript
 *
 * `fetch` rather than the shared axios instance, and only here: axios in the
 * browser buffers the whole response before resolving, which turns a stream into
 * one delivery at the end — the exact failure the backend sets
 * `X-Accel-Buffering: no` to avoid. The list and transcript calls stay on axios.
 */

// One anonymous key names ONE conversation, not one browser: the column is
// UNIQUE server-side, so a second anonymous thread mints a second key. The store
// is therefore thread_id -> key, plus the key of the thread currently in flight
// (which has no id until the `thread` event arrives).
const KEY_STORE = "fixydany_dany_keys";
const PENDING_KEY = "fixydany_dany_pending_key";

function readKeys() {
  try {
    const raw = window.localStorage.getItem(KEY_STORE);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeKeys(keys) {
  try {
    window.localStorage.setItem(KEY_STORE, JSON.stringify(keys));
  } catch {
    /* private mode, quota — Dany still works, the thread just is not resumable */
  }
}

export function anonymousKeyFor(threadId) {
  if (!threadId) return null;
  return readKeys()[threadId] || null;
}

export function rememberAnonymousKey(threadId, key) {
  if (!threadId || !key) return;
  const keys = readKeys();
  keys[threadId] = key;
  writeKeys(keys);
}

/**
 * Drop the key for a thread that now belongs to an account (DY2.1).
 *
 * Claiming clears `anonymous_key` server-side, so from that moment every
 * anonymous read of the thread 404s — verified against production:
 *
 *     GET /dany/threads/dthr_b0055641d701  X-Dany-Anonymous-Key: danon_…
 *     404 {"detail":"Conversation not found"}
 *
 * Keeping the dead key means the browser goes on presenting a credential the
 * server has retired, and any request made before the session cookie is in play
 * gets that 404 — which the thread screen used to render as an empty
 * conversation. The key is useless the instant registration succeeds; deleting
 * it is the honest bookkeeping.
 */
export function forgetAnonymousKey(threadId) {
  if (!threadId) return;
  const keys = readKeys();
  if (!(threadId in keys)) return;
  delete keys[threadId];
  writeKeys(keys);
}

/** Every anonymous key this browser holds, newest last. Used to list threads. */
export function allAnonymousKeys() {
  return Object.values(readKeys());
}

export function setPendingKey(key) {
  try {
    if (key) window.localStorage.setItem(PENDING_KEY, key);
    else window.localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

function getPendingKey() {
  try {
    return window.localStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

/**
 * Split a byte stream into SSE frames.
 *
 * The backend emits `event: <name>\ndata: <json>\n\n`. Frames are separated by a
 * blank line and a single frame can straddle two network chunks, so the tail of
 * the buffer is carried forward rather than parsed.
 */
function parseFrames(buffer) {
  const frames = [];
  let rest = buffer;
  let split = rest.indexOf("\n\n");
  while (split !== -1) {
    const raw = rest.slice(0, split);
    rest = rest.slice(split + 2);
    split = rest.indexOf("\n\n");

    let event = "message";
    const dataLines = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      // `:` comment lines and anything else are ignored, per the SSE spec.
    }
    if (!dataLines.length) continue;
    try {
      frames.push({ event, data: JSON.parse(dataLines.join("\n")) });
    } catch {
      // A frame we cannot parse is dropped rather than killing the turn: the
      // stream is still well-formed and the next frame may be the answer.
    }
  }
  return { frames, rest };
}

/**
 * Send one turn and stream the answer.
 *
 * `onEvent(name, data)` is called for every SSE event in order:
 * `thread` → `sources` → `token`* → `tool_call` → `tool_result` → `done`,
 * or a terminal `error` with no `done` after it.
 *
 * Returns an object with `abort()`. Aborting mid-turn is safe: the backend's
 * `session_scope` rolls the turn back, so no half-transcript is persisted.
 */
export function streamChat({ message, threadId, mode, jobId, language, attachments, onEvent, signal }) {
  const controller = new AbortController();
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });

  const headers = { "Content-Type": "application/json" };
  // Continuing an anonymous thread replays its key; starting a new one sends
  // nothing, because the server mints a fresh key per conversation and reusing
  // one is what a unique-violation looks like the second time around.
  const key = threadId ? anonymousKeyFor(threadId) : null;
  if (key) headers["X-Dany-Anonymous-Key"] = key;

  const done = (async () => {
    let response;
    try {
      response = await fetch(`${API}/dany/chat`, {
        method: "POST",
        credentials: "include",
        headers,
        signal: controller.signal,
        body: JSON.stringify({
          message,
          thread_id: threadId || null,
          mode: mode || null,
          // R7. Travels beside `mode` and is honoured on the same terms: only
          // when this request creates the thread, and only after the server
          // re-checks it against the row. It says which job the conversation
          // was opened from, so the quote draft is born bound to that job
          // rather than to one reconstructed from a title.
          job_id: jobId || null,
          language: language || null,
          // T19 F4. The photos on this turn, by the `file_id` the upload
          // returned. Part of the user's message, not a separate request: the
          // server writes them on the same row as the words.
          attachments: (attachments || []).map((a) => ({ file_id: a.file_id })),
        }),
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      onEvent("error", { code: "network", message: String(err) });
      return;
    }

    if (!response.ok || !response.body) {
      // 429 from slowapi is the one status worth naming: the shell shows the
      // rate-limit copy instead of the generic failure line. A refusal that
      // names its own code — the text switch's 503 AIDisabled (DY1.7 M5) —
      // keeps it, so it reads as that line and not as a generic failure.
      let detail = null;
      try {
        const body = await response.json();
        detail = body && typeof body.detail === "object" ? body.detail : null;
      } catch {
        /* not JSON — a proxy page; the status code is all there is */
      }
      onEvent("error", {
        code: detail?.code
          || (response.status === 429 ? "rate_limited" : `http_${response.status}`),
        message: detail?.message || `Dany replied ${response.status}.`,
      });
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    // A turn ends with `done` or with `error`. Anything else — the backend
    // restarting mid-answer, a proxy cutting an idle stream, a phone changing
    // network — used to end the loop silently: the text simply stopped and the
    // screen showed no reason. Tracking the terminator is what turns that into
    // a named failure the caller can report and retry.
    let terminated = false;
    try {
      for (;;) {
        const { value, done: finished } = await reader.read();
        if (finished) break;
        buffer += decoder.decode(value, { stream: true });
        const { frames, rest } = parseFrames(buffer);
        buffer = rest;
        for (const frame of frames) {
          if (frame.event === "thread") {
            // A new anonymous conversation gets its key exactly once, here.
            if (frame.data.anonymous_key) {
              setPendingKey(frame.data.anonymous_key);
              rememberAnonymousKey(frame.data.thread_id, frame.data.anonymous_key);
              setPendingKey(null);
            }
          }
          if (frame.event === "done" || frame.event === "error") terminated = true;
          onEvent(frame.event, frame.data);
        }
      }
      if (!terminated && !controller.signal.aborted) {
        onEvent("error", {
          code: "stream_broken",
          message: "The stream ended without a done frame.",
        });
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        onEvent("error", { code: "stream_broken", message: String(err) });
      }
    }
  })();

  return { abort: () => controller.abort(), done };
}

/**
 * The caller's conversations.
 *
 * Signed-in callers get theirs by user id and the header is ignored. An
 * anonymous visitor's threads are keyed one-per-key, so the list is assembled by
 * asking once per key this browser holds — there is no server-side "all my
 * anonymous threads" and inventing one would mean a browser-wide identifier the
 * backend deliberately does not have.
 */
export async function listThreads({ authenticated }) {
  if (authenticated) {
    const { data } = await api.get("/dany/threads");
    return data.threads || [];
  }
  const keys = allAnonymousKeys();
  const pending = getPendingKey();
  if (pending && !keys.includes(pending)) keys.push(pending);
  const results = await Promise.allSettled(
    keys.map((key) => api.get("/dany/threads", { headers: { "X-Dany-Anonymous-Key": key } }))
  );
  const threads = [];
  const seen = new Set();
  for (const result of results) {
    if (result.status !== "fulfilled") continue;
    for (const thread of result.value.data.threads || []) {
      if (seen.has(thread.thread_id)) continue;
      seen.add(thread.thread_id);
      threads.push(thread);
    }
  }
  threads.sort((a, b) =>
    String(b.last_message_at || b.created_at).localeCompare(String(a.last_message_at || a.created_at))
  );
  return threads;
}

/** One conversation, whole. 404 covers both "gone" and "not yours". */
export async function loadThread(threadId) {
  const key = anonymousKeyFor(threadId);
  const { data } = await api.get(`/dany/threads/${threadId}`, {
    headers: key ? { "X-Dany-Anonymous-Key": key } : undefined,
  });
  return data;
}

/** Page context → the mode hint the home and the FAB open in (DANY.md §2.5). */
export const MODE_GENERAL = "general";
export const MODE_CLIENT = "client";
export const MODE_PROVIDER = "provider";

/**
 * What Dany remembers about the signed-in user (DY1.5).
 *
 * Signed-in only, and the endpoint says so with a 401 rather than an empty
 * list — "Dany remembers nothing about you" and "you are not signed in" are
 * different answers and the UI shows different things for them.
 */
export async function listMemories() {
  const { data } = await api.get("/dany/memories");
  return data.memories || [];
}

/** Forget one thing. Takes effect on the next thread, not retroactively. */
export async function deleteMemory(memoryId) {
  await api.delete(`/dany/memories/${memoryId}`);
}

/**
 * Commit a proposed write (DY2.1).
 *
 * `actionId` is the whole authorization: it was minted server-side when Dany
 * proposed, and this is the only call that can spend it. There is no variant
 * that executes without one, which is what makes the confirmation a rule rather
 * than a convention — see routes/dany.py.
 *
 * `payload` is the draft as edited on screen. The server keeps only the keys it
 * proposed, so sending the whole draft back is safe.
 *
 * Throws with `.code` set from the backend's structured detail, so the caller
 * picks copy by code instead of parsing a sentence that exists in two languages.
 * The two codes that are not failures: `registration_required` (create an
 * account, then call again) and `email_verification_required` (confirm the
 * address; the job posts by itself when they do).
 */
export async function executeAction(actionId, payload, { anonymousKey } = {}) {
  try {
    const { data } = await api.post(
      `/dany/actions/${actionId}/execute`,
      { payload: payload || null },
      anonymousKey ? { headers: { "X-Dany-Anonymous-Key": anonymousKey } } : undefined
    );
    return data;
  } catch (err) {
    throw actionError(err);
  }
}

/** Discard a proposal. Recorded, not deleted — see routes/dany.py. */
export async function rejectAction(actionId, { anonymousKey } = {}) {
  try {
    const { data } = await api.post(
      `/dany/actions/${actionId}/reject`,
      {},
      anonymousKey ? { headers: { "X-Dany-Anonymous-Key": anonymousKey } } : undefined
    );
    return data;
  } catch (err) {
    throw actionError(err);
  }
}

/**
 * An axios failure, flattened to something with a `code`.
 *
 * FastAPI puts our structured refusal in `detail`; slowapi's 429 does not have
 * one, and a proxy failing in front of the app has neither. All three end up
 * here so the widget has exactly one shape to render.
 */
function actionError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const error = new Error(
    (detail && detail.message) || err?.message || "Request failed"
  );
  error.code =
    (detail && detail.code) ||
    (status === 429 ? "action_rate_limited" : `http_${status || "network"}`);
  error.status = status;
  error.email = detail && detail.email;
  // The whole structured refusal, for the cards that render more than its
  // code — the out-of-quotes card reads `extra_price_usd` and `resets_at`.
  error.detail = detail && typeof detail === "object" ? detail : null;
  return error;
}
